import type { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import { Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import ChakraReactTable from "components/Shared/ChakraReactTable";
import {
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import { useQuery } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { addActiveBrandIdToContext } from "utils/apollo/hooks/brand";
import { INVALID_UUID } from "utils/misc";
import { useOptionalCombineFilters } from "utils/useCombineFilters";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import TableControlsFooter from "components/Shared/ChakraReactTable/TableControlsFooter";
import NumResultsLabel from "components/Shared/ChakraReactTable/NumResultsLabel";
import TableControls from "components/Shared/ChakraReactTable/TableControls";
import PaginationControls from "components/Shared/ChakraReactTable/PaginationControls";
import {
  formatAddressCell,
  formatExternalDomainCell,
  formatIntegerCell,
  makeFormatDateCell,
  makeFormatEnumCell,
} from "components/Shared/ChakraReactTable/formatters";
import useArchivedRowProps from "components/Shared/ChakraReactTable/useArchivedRowProps";
import { CONTENT_FULL_HEIGHT } from "components/Layout/constants";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import ScrollContainer from "components/Shared/ChakraReactTable/ScrollContainer";
import {
  NameWithFlagIcons,
  pricingStrategyMap,
  retailerCategoryReviewMap,
  slottingOptionsMap,
} from "components/Shared/RetailerDirectory/utils";
import TagList from "components/Shared/TagList";
import { RetailerFilter, RetailersOrderBy } from "__generated__/gql/graphql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import { useCurrentBrandId } from "utils/currentUserHooks";
import RetailerTypeBadge from "./RetailerTypeBadge";
import IsCustomBadge from "./IsCustomBadge";
import RetailerBrokerIcons, {
  RetailerBrokerIconsFragment,
} from "./RetailerBrokerIcons";

export const fragment = gql(/* GraphQL */ `
  fragment RetailersDirectoryTableRow on Retailer {
    id
    nameWithUniqueSuffix
    numStores
    website
    retailerType
    slottingRequired
    pricingStrategy
    categoryReview
    isArchived
    isCustom
    customCreatedAt
    channelsList {
      id
      name
    }
    regionsList {
      id
      name
    }
    headquartersAddress {
      id
      ...AddressComponentFields
    }
    ...RetailerNameWithFlagIconsFragment
    ...RetailerIsCustomBadge
  }
`);

const query = gql(/* GraphQL */ `
  query RetailersDirectoryTable(
    $isAdmin: Boolean = false
    $brandId: UUID!
    $filter: RetailerFilter
    $orderBy: [RetailersOrderBy!] = [NAME_ASC]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    retailersForBrand(
      brandId: $brandId
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) @skip(if: $isAdmin) {
      totalCount
      nodes {
        id
        ...RetailersDirectoryTableRow
        ...RetailerBrokerIcons
      }
    }
    retailers(
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) @include(if: $isAdmin) {
      totalCount
      nodes {
        id
        ...RetailersDirectoryTableRow
      }
    }
  }
`);

export type Datum = DocumentType<typeof fragment> &
  Partial<RetailerBrokerIconsFragment>;

const col = createColumnHelper<Datum>();

export const DEFAULT_COLUMNS = [
  col.accessor("nameWithUniqueSuffix", {
    id: "name",
    header: "Retailer Name",
    cell: (cell) => (
      <NameWithFlagIcons retailer={cell.row.original}>
        <RetailerBrokerIcons retailer={cell.row.original} />
      </NameWithFlagIcons>
    ),
    size: 250,
  }),
  col.accessor("retailerType", {
    header: "Type",
    cell: (cell) => (
      <RetailerTypeBadge
        retailerType={cell.getValue()}
        isTruncated
        maxW="100%"
        fontSize=".8em"
        lineHeight={1.5}
      />
    ),
    size: 130,
    meta: { props: { textAlign: "center" }, cellProps: { isTruncated: true } },
  }),
  col.accessor("website", {
    header: "Website",
    cell: formatExternalDomainCell,
    size: 200,
    meta: { cellProps: { isTruncated: true } },
  }),
  col.accessor("numStores", {
    header: "Stores",
    cell: formatIntegerCell,
    size: 110,
    meta: {
      headerProps: { textAlign: "center" },
      cellProps: { isNumeric: true },
    },
  }),
  col.accessor("headquartersAddress", {
    header: "Headquarters",
    cell: formatAddressCell,
    size: 225,
  }),
  col.accessor("channelsList", {
    header: "Channels",
    cell: (cell) => {
      const channels = cell.getValue();
      return (
        <TagList
          items={channels.slice(0, 4)}
          numTotal={channels.length}
          getKey={(c) => c.id}
          getLabel={(c) => c.name}
          isTruncated
        />
      );
    },
    enableSorting: false,
  }),
  col.accessor("regionsList", {
    header: "Regions",
    cell: (cell) => {
      const regions = cell.getValue();
      return (
        <TagList
          items={regions.slice(0, 4)}
          numTotal={regions.length}
          getKey={(c) => c.id}
          getLabel={(c) => c.name}
          isTruncated
        />
      );
    },
    enableSorting: false,
    size: 250,
  }),
  col.accessor("slottingRequired", {
    header: "Slotting Required?",
    cell: makeFormatEnumCell(slottingOptionsMap),
    size: 200,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("pricingStrategy", {
    header: "Pricing Strategy",
    cell: makeFormatEnumCell(pricingStrategyMap),
    size: 200,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("categoryReview", {
    header: "Category Review",
    cell: makeFormatEnumCell(retailerCategoryReviewMap),
    size: 200,
    meta: { props: { textAlign: "center" } },
  }),
];

export const DEFAULT_BRAND_COLUMNS = [
  ...DEFAULT_COLUMNS,
  col.accessor("customCreatedAt", {
    header: "Created",
    cell: makeFormatDateCell("PP"),
    size: 135,
    meta: { props: { textAlign: "right" } },
  }),
  col.accessor((r) => r.isCustom, {
    id: "isCustom",
    header: "Custom?",
    cell: (cell) => <IsCustomBadge retailer={cell.row.original} />,
    size: 120,
    meta: { props: { textAlign: "center" } },
  }),
];

export const DEFAULT_PINNED_COLUMNS: ColumnPinningState = { left: ["name"] };

const BASE_FILTER: RetailerFilter = {
  or: [
    { isCustom: { equalTo: false }, isArchived: { equalTo: false } },
    { isCustom: { equalTo: true } },
  ],
};

export const orderByEnumsById: GQLOrderByEnumsById<RetailersOrderBy> = {
  name: [
    RetailersOrderBy.NAME_WITH_UNIQUE_SUFFIX_ASC,
    RetailersOrderBy.NAME_WITH_UNIQUE_SUFFIX_DESC,
  ],
  retailerType: [
    RetailersOrderBy.RETAILER_TYPE_ASC,
    RetailersOrderBy.RETAILER_TYPE_DESC,
  ],
  website: [RetailersOrderBy.WEBSITE_ASC, RetailersOrderBy.WEBSITE_DESC],
  numStores: [
    RetailersOrderBy.NUM_STORES_ASC,
    RetailersOrderBy.NUM_STORES_DESC,
  ],
  headquartersAddress: [
    RetailersOrderBy.HEADQUARTERS_ADDRESS__ZIP_ASC,
    RetailersOrderBy.HEADQUARTERS_ADDRESS__ZIP_DESC,
  ],
  slottingRequired: [
    RetailersOrderBy.SLOTTING_REQUIRED_ASC,
    RetailersOrderBy.SLOTTING_REQUIRED_DESC,
  ],
  pricingStrategy: [
    RetailersOrderBy.PRICING_STRATEGY_ASC,
    RetailersOrderBy.PRICING_STRATEGY_DESC,
  ],
  categoryReview: [
    RetailersOrderBy.CATEGORY_REVIEW_ASC,
    RetailersOrderBy.CATEGORY_REVIEW_DESC,
  ],
  customCreatedAt: [
    RetailersOrderBy.CUSTOM_CREATED_AT_ASC,
    RetailersOrderBy.CUSTOM_CREATED_AT_DESC,
  ],
  isCustom: [RetailersOrderBy.IS_CUSTOM_ASC, RetailersOrderBy.IS_CUSTOM_DESC],
};

export type RetailersDirectoryTableProps = Partial<
  Pick<TableOptions<Datum>, "columns" | "meta">
> & {
  filter?: RetailerFilter;
  isAdmin?: boolean;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onSelectedChange: (id: UUID) => void;
  pinnedColumns?: ColumnPinningState;
  selected?: UUID | null;
};

export default function RetailersDirectoryTable({
  columns = DEFAULT_BRAND_COLUMNS,
  filter: filterProp,
  isAdmin = false,
  meta,
  nextFetchPolicy,
  onSelectedChange,
  pinnedColumns: columnPinning = DEFAULT_PINNED_COLUMNS,
  selected,
}: RetailersDirectoryTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination();

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "name", desc: false }],
  );

  const brandId = useCurrentBrandId();

  const filter = useOptionalCombineFilters(
    isAdmin ? undefined : BASE_FILTER,
    filterProp,
  );

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useQuery(query, {
    nextFetchPolicy,
    context: isAdmin ? ADMIN_CONTEXT : addActiveBrandIdToContext(brandId),
    skip:
      !(isAdmin || brandId) || isPendingFilterReset || isPendingSortingReset,
    variables: {
      isAdmin,
      brandId: isAdmin ? INVALID_UUID : brandId || INVALID_UUID,
      filter,
      orderBy,
      ...limitOffset,
    },
  });

  const { nodes, totalCount } =
    rawData?.retailersForBrand || rawData?.retailers || {};

  const tableInstance = useReactTable<Datum>({
    columns,
    data: usePrepApolloData(getFragmentData(fragment, nodes) as Datum[]),
    meta,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useChakraReactTableProps<Datum>([
    useStickyHeader(),
    useFixedLayout(tableInstance),
    useSelectableRow({ selected, onSelectedChange }),
    useArchivedRowProps(),
    {
      tableProps: { fontSize: "sm" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
  ]);

  return (
    <>
      <ScrollContainer
        minHeight="15rem"
        maxHeight={`calc(${CONTENT_FULL_HEIGHT} - 15.5rem)`}
        borderWidth={1}
        borderTopRadius=".25rem"
      >
        <ChakraReactTable
          tableInstance={tableInstance}
          {...tableProps}
          isLoading={isLoading || typeof window === "undefined"}
          noRowsPlaceholder={<Text>No retailers found.</Text>}
        />
      </ScrollContainer>
      <TableControlsFooter
        py={4}
        borderTopWidth={1}
        leftContent={
          <NumResultsLabel
            {...{ totalCount, isLoading }}
            resultTypeLabel="retailers"
          />
        }
        rightContent={<TableControls {...{ isLoading }} onRefresh={refetch} />}
      >
        <PaginationControls {...{ tableInstance, isLoading }} />
      </TableControlsFooter>
    </>
  );
}
